import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, Printer, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (p) => {
  const r = (p || 0) / 100;
  return "₹" + r.toLocaleString("en-IN", r % 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 0 });
};
const label = "block font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] mb-2";

export default function TicketPage() {
  const [params] = useSearchParams();
  const txnid = params.get("txnid");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const [ctype, setCtype] = useState("name_change");
  const [cToken, setCToken] = useState("");
  const [nf, setNf] = useState("");
  const [nl, setNl] = useState("");
  const [ne, setNe] = useState("");
  const [np, setNp] = useState("");
  const [cBusy, setCBusy] = useState(false);
  const [cMsg, setCMsg] = useState("");
  const [cErr, setCErr] = useState("");

  useEffect(() => {
    if (!txnid) {
      setErr("Missing booking reference.");
      return;
    }
    fetch(`${API}/bookings/status/${txnid}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.detail || "Booking not found");
        setData(d);
        if (d.tickets?.length) setCToken(d.tickets[0].token);
      })
      .catch((e) => setErr(e.message));
  }, [txnid]);

  const paid = data?.status === "paid";
  const flex = data && (data.addon === "lite_flex" || data.addon === "full_flex");
  const changeState = data?.changes?.[ctype];

  const submitChange = async () => {
    setCErr("");
    setCMsg("");
    if (!nf.trim() || !nl.trim()) { setCErr("Enter the new participant's full name"); return; }
    if (ctype === "transfer" && (!ne.trim() || !np.trim())) { setCErr("A transfer needs the new participant's email and mobile"); return; }
    setCBusy(true);
    try {
      const res = await fetch(`${API}/ticket/change-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: cToken,
          type: ctype,
          new_first_name: nf.trim(),
          new_last_name: nl.trim(),
          new_email: ctype === "transfer" ? ne.trim() : null,
          new_phone: ctype === "transfer" ? np.trim() : null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Request failed");
      setCMsg("Request sent — the organizer will confirm by email. Your current ticket stays valid meanwhile.");
      setNf(""); setNl(""); setNe(""); setNp("");
    } catch (e) {
      setCErr(e.message);
    } finally {
      setCBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#161614] text-[#f0eee4] font-body antialiased px-6 py-12" data-testid="ticket-page">
      <div className="max-w-2xl mx-auto">
        <img src="/logo-long.png" alt="TORQ Circuit" className="h-6 w-auto" />

        {!data && !err && (
          <div className="mt-16 flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-[#a3a39f]" data-testid="ticket-loading">
            <Loader2 size={16} className="animate-spin" /> CHECKING YOUR BOOKING…
          </div>
        )}

        {err && (
          <div className="mt-16 border border-[#2e2e2a] bg-[#1e1e1c] p-8" data-testid="ticket-error">
            <span className="inline-block border border-[#454540] p-3"><X size={18} /></span>
            <h1 className="mt-6 font-display font-extrabold uppercase text-4xl">CAN'T FIND IT.</h1>
            <p className="mt-3 text-sm text-[#a3a39f]">{err}</p>
          </div>
        )}

        {data && !paid && (
          <div className="mt-16 border border-[#2e2e2a] bg-[#1e1e1c] p-8" data-testid="ticket-unpaid">
            <span className="inline-block border border-[#454540] p-3"><X size={18} /></span>
            <h1 className="mt-6 font-display font-extrabold uppercase text-4xl" data-testid="ticket-status">PAYMENT NOT COMPLETED.</h1>
            <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed">
              No confirmed payment for this booking ({data.status}). If money was debited, it is
              reversed automatically by your bank — head back and book again.
            </p>
            <Link to="/register" data-testid="ticket-retry" className="btn-solid block text-center mt-8 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white">
              TRY AGAIN
            </Link>
          </div>
        )}

        {data && paid && (
          <div data-testid="ticket-confirmed">
            <div className="mt-16">
              <span className="inline-block border border-[#f0eee4] p-3"><Check size={18} /></span>
              <h1 className="mt-6 font-display font-extrabold uppercase text-5xl leading-none" data-testid="ticket-status">
                BOOKING CONFIRMED.
              </h1>
              <p className="mt-3 text-sm text-[#a3a39f] leading-relaxed">
                Your TORQ CIRCUIT registration is confirmed. Your confirmation email contains your unique QR code —
                keep it accessible on your phone or bring a printed copy for race-day verification and collection.
                Venue details, reporting times, race waves, bib collection and wristband procedures will be announced
                on this website and our Instagram page on the day of the event — follow{" "}
                <a href="https://www.instagram.com/torqcircuit/" target="_blank" rel="noreferrer" className="text-[#f0eee4] underline underline-offset-4">instagram.com/torqcircuit</a>{" "}
                for the latest announcements.
              </p>
              <div className="mt-8 border border-[#2e2e2a] font-mono text-[11px] tracking-[0.15em]">
                <div className="flex justify-between px-5 py-3 border-b border-[#2e2e2a]">
                  <span className="text-[#6e6e6a]">BOOKING REF</span>
                  <span className="font-bold text-sm" data-testid="ticket-booking-ref">{data.booking_ref}</span>
                </div>
                <div className="flex justify-between px-5 py-3 border-b border-[#2e2e2a]">
                  <span className="text-[#6e6e6a]">CATEGORY</span><span data-testid="ticket-category">{data.category_label}</span>
                </div>
                <div className="flex justify-between px-5 py-3">
                  <span className="text-[#6e6e6a]">PAID</span><span data-testid="ticket-amount">{inr(data.total_paise)}</span>
                </div>
              </div>
            </div>

            <p className="mt-10 font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a]">YOUR QR TICKETS — ONE PER PARTICIPANT</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-px bg-[#2e2e2a] border border-[#2e2e2a]">
              {data.tickets.map((t, i) => (
                <div key={t.token} className="bg-[#161614] p-6 text-center" data-testid={`ticket-qr-${i}`}>
                  <img src={t.qr} alt={`QR ticket for ${t.name}`} className="w-48 h-48 mx-auto bg-white p-2" data-testid={`ticket-qr-img-${i}`} />
                  <p className="mt-4 font-display font-bold uppercase text-xl">{t.name}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]">
                    {data.category_label} — {data.booking_ref}
                  </p>
                  <a
                    href={`${API}/tickets/qr/${t.token}.png`}
                    download={`TORQ-${data.booking_ref}-${t.name.replace(/\s+/g, "-")}.png`}
                    data-testid={`ticket-download-${i}`}
                    className="mt-4 inline-block border border-[#454540] font-mono text-[10px] tracking-[0.2em] px-4 py-2.5 min-h-[44px] hover:border-[#f0eee4] transition-colors"
                  >
                    DOWNLOAD QR
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => window.print()}
                data-testid="ticket-print"
                className="btn-solid flex-1 bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white inline-flex items-center justify-center gap-2"
              >
                <Printer size={14} /> SAVE / PRINT
              </button>
              <Link to="/" data-testid="ticket-back" className="btn-solid border border-[#454540] font-mono text-xs tracking-[0.25em] px-6 py-4 hover:border-[#f0eee4] text-center">
                HOME
              </Link>
            </div>

            {flex && data.changes && (
              <div className="mt-10 border border-[#2e2e2a] p-6" data-testid="ticket-changes">
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#6e6e6a]">
                  {data.addon === "full_flex" ? "FULL FLEX" : "LITE FLEX"} — TICKET CHANGES
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {data.tickets.length > 1 && (
                    <div>
                      <label className={label}>ATHLETE</label>
                      <select className="field" data-testid="change-athlete" value={cToken} onChange={(e) => setCToken(e.target.value)}>
                        {data.tickets.map((t) => <option key={t.token} value={t.token}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className={label}>CHANGE TYPE</label>
                    <select className="field" data-testid="change-type" value={ctype} onChange={(e) => setCtype(e.target.value)}>
                      <option value="name_change">NAME CHANGE</option>
                      {data.addon === "full_flex" && <option value="transfer">TRANSFER TO ANOTHER PERSON</option>}
                    </select>
                  </div>
                  <div>
                    <label className={label}>NEW FIRST NAME</label>
                    <input className="field" data-testid="change-first" value={nf} onChange={(e) => setNf(e.target.value)} />
                  </div>
                  <div>
                    <label className={label}>NEW LAST NAME</label>
                    <input className="field" data-testid="change-last" value={nl} onChange={(e) => setNl(e.target.value)} />
                  </div>
                  {ctype === "transfer" && (
                    <>
                      <div>
                        <label className={label}>NEW PARTICIPANT EMAIL</label>
                        <input className="field" data-testid="change-email" type="email" value={ne} onChange={(e) => setNe(e.target.value)} />
                      </div>
                      <div>
                        <label className={label}>NEW PARTICIPANT MOBILE</label>
                        <input className="field" data-testid="change-phone" inputMode="numeric" value={np} onChange={(e) => setNp(e.target.value)} />
                      </div>
                    </>
                  )}
                </div>

                {changeState?.allowed ? (
                  <button
                    onClick={submitChange}
                    disabled={cBusy}
                    data-testid="change-submit"
                    className="mt-5 w-full btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 min-h-[52px] hover:bg-white disabled:opacity-40"
                  >
                    {cBusy ? "SENDING…" : "REQUEST CHANGE"}
                  </button>
                ) : (
                  <p className="mt-5 font-mono text-[11px] tracking-[0.1em] text-[#a3a39f] border border-[#454540] px-4 py-3" data-testid="change-unavailable">
                    {ctype === "name_change" ? "Name change" : "Transfer"} unavailable: {changeState?.reason || "not allowed"}
                  </p>
                )}
                {cErr && <p className="mt-3 font-mono text-[11px] tracking-[0.1em] text-red-400" data-testid="change-error">{cErr}</p>}
                {cMsg && <p className="mt-3 font-mono text-[11px] tracking-[0.1em] text-[#f0eee4] border border-[#454540] px-4 py-3" data-testid="change-success">{cMsg}</p>}
                <p className="mt-4 font-mono text-[9px] tracking-[0.08em] text-[#6e6e6a] leading-relaxed">
                  One change per plan, before the deadline in the Ticket Protection &amp; Refund Policy. The organizer approves every request.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
